/**
 * `DimeCloudApp` — the batteries-included cloud terminal: Clerk sign-in
 * (email + enabled socials, e.g. "Continue with Google") gating the full DIME
 * cloud flow (`DimeCloudTerminal`). A host supplies a base terminal config;
 * everything else — sign-in UI, identity exchange, access-code onboarding,
 * gateway-grant resolution, reconnect recovery — ships in the package.
 *
 * **Requires `<DimeTerminalProvider>` above it in the tree.** The provider
 * owns the single `ClerkProvider` instance and distributes the environment /
 * apiUrl context. This prevents the "multiple ClerkProvider" error when two
 * `<DimeCloudApp>` surfaces transition (e.g. drawer → fullscreen route).
 *
 * Two host-integration seams (inert when omitted):
 *  - `silentExchange`: a non-Clerk DIME-JWT exchange attempted BEFORE any
 *    sign-in UI — the linked-account fast path (a host platform token →
 *    DIME JWT via POST /v1/auth/identity; see
 *    `createPlatformTokenExchange`). Success ⇒ the flow runs on it and Clerk
 *    never mounts for the session; rejection/failure ⇒ fall through to the
 *    Clerk sign-in.
 *  - `renderLinkPrompt`: rendered once after a successful Clerk sign-in so a
 *    host can offer "link your account" (enabling the silent path on future
 *    loads). The ctx carries `linkIdentity(platformToken)` — the package
 *    performs the link with a fresh Clerk-derived DIME JWT, the host only
 *    supplies the platform token it alone can mint; `dismiss` continues to
 *    the terminal.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Show, SignIn, useAuth, useClerk, useUser } from '@clerk/react';
import styled from 'styled-components';

import type { TerminalConfig } from '../config.js';
import { resolveDimeApiUrl } from './environments.js';
import {
  createClerkTokenExchange,
  linkPlatformIdentity,
  selfUnlinkPlatformIdentity,
  type DimeTokenExchange,
  type IdentityLink,
} from './identity.js';
import {
  DimeCloudTerminal,
  type CloudTerminalBaseConfig,
  type DimeCloudTerminalProps,
} from './DimeCloudTerminal.js';
import { AuthShell } from './AuthShell.js';
import { useDimeTerminalContext } from './DimeTerminalProvider.js';
import { sessionCacheKey } from './sessionCache.js';

type ClerkAppearance = ComponentProps<typeof SignIn>['appearance'];

/**
 * Default light theme for the Clerk widget, matching the terminal's palette.
 */
const DEFAULT_CLERK_APPEARANCE: ClerkAppearance = {
  variables: {
    colorPrimary: '#6d28d9', // purple (buttons, links, focus)
    colorBackground: '#ffffff', // card background (light)
    colorForeground: '#16191c', // primary text
    colorMutedForeground: '#5f666d', // muted/secondary text
    colorInput: '#f4f5f7', // input field background
    colorInputForeground: '#16191c', // text typed into inputs
    colorPrimaryForeground: '#ffffff', // text on the purple button
    // Neutral drives social-button text/borders; light themes need a
    // dark shade so "Continue with Google" text is readable.
    colorNeutral: '#16191c',
  },
  elements: {
    // Belt-and-suspenders: force the social button text/border dark.
    socialButtonsBlockButton: {
      color: '#16191c',
      borderColor: '#d7dade',
    },
    socialButtonsBlockButtonText: { color: '#16191c' },
  },
};

/**
 * Host config minus the fields `DimeCloudApp` derives from the session.
 *
 * `onUpdateProfile` is in this list because the identity is Clerk's here: the
 * component always passes its own Clerk-backed writer, so a host-supplied one
 * would be silently replaced. Excluding it at the type level turns that into a
 * compile error rather than a handler that never runs.
 */
export type CloudAppBaseConfig = Omit<
  CloudTerminalBaseConfig,
  'accountId' | 'onLogout' | 'onUpdateProfile' | 'user'
>;

export interface DimeCloudAppProps {
  readonly clerk?: {
    /** Clerk widget theming; defaults to the packaged dark theme. */
    readonly appearance?: ClerkAppearance;
  };
  /** Host terminal config; connection + session identity fields are derived. */
  readonly config: CloudAppBaseConfig;
  /** Passthrough phase-UI override (see `DimeCloudTerminal`). */
  readonly renderPhase?: DimeCloudTerminalProps['renderPhase'];
  /**
   * Same contract as `TerminalProps.agentEffects`: host nodes rendered inside
   * the agent context (so they can call `useTerminalAgent()` /
   * `useTerminalAuth()`) but outside the visible layout — they should render
   * null into the layout (portals are fine). Forwarded on both the silent
   * fast path and the Clerk path.
   */
  readonly agentEffects?: DimeCloudTerminalProps['agentEffects'];
  /** Same contract as `TerminalProps.compact`: hide the package top bar for space-constrained embeds. */
  readonly compact?: DimeCloudTerminalProps['compact'];
  /**
   * Hide the signed-in email + Log Out bar (`AuthShell`). For embedded hosts
   * whose own chrome frames the terminal — sign-out stays reachable via the
   * terminal sidebar's account menu (`config.onLogout` is still wired).
   * Default false.
   */
  readonly hideAuthShell?: boolean;
  /**
   * Linked-account fast path: a non-Clerk exchange (a host platform token →
   * DIME JWT — see `createPlatformTokenExchange`) attempted silently before
   * any sign-in UI. Success skips Clerk for the session; rejection/failure
   * falls through to the Clerk sign-in. Omit ⇒ straight to Clerk.
   */
  readonly silentExchange?: {
    readonly exchange: DimeTokenExchange;
    /**
     * Account id for the silent path (no Clerk user to derive one from).
     * Must be STABLE per user (e.g. the wallet address / platform user id):
     * it keys the provider's session cache and the local storage buckets —
     * an id that changes per mount defeats remount reuse entirely.
     */
    readonly accountId: string;
    /** Display identity for the silent path (sidebar account button etc.). */
    readonly user?: TerminalConfig['user'];
    /**
     * Host action behind the menu's escape item. Without `mintPlatformToken`
     * it renders as "Log Out" and runs verbatim; with it, the packaged
     * "Unlink account" action replaces it and this runs AFTER a successful
     * unlink as the host's cleanup hook. Omit both ⇒ the menu hides the item.
     */
    readonly onLogout?: () => void;
    /**
     * Enables the packaged "Unlink account" action on the silent path
     * (sidebar account menu + stuck-phase escape link) in place of "Log Out"
     * — a tethered session has nothing to log out of; the next mount would
     * just silently sign back in. The action mints a FRESH platform token
     * via this callback (5-minute TTL — same callback shape as the exchange
     * mint), severs the link server-side (self-unlink: the platform token is
     * the only credential needed), then drops this session to the sign-in
     * page. Future silent exchanges refuse with IDENTITY_NOT_LINKED until
     * the user links again. If the mint or unlink call fails, the session
     * stays put (dropping to sign-in anyway would sign straight back in,
     * which reads as "unlink didn't work") and a dismissible error notice is
     * shown over the terminal.
     */
    readonly mintPlatformToken?: () => Promise<string | null>;
  };
  /**
   * Linking seam (in-flow gate): rendered once after a successful Clerk
   * sign-in so a host can offer "link your account" (enabling the silent
   * fast path on future loads) without any host-chrome work. Pass it only
   * when there is something to link — e.g. this session's silent exchange
   * failed with IDENTITY_NOT_LINKED and the platform user is signed in.
   * `dismiss` continues to the terminal. Omit ⇒ no prompt.
   *
   * Hosts that want the prompt in their OWN chrome (banner/popup outside
   * the terminal surface) should use `useDimeLinkAccount()` instead — same
   * operation, host-placed UI.
   */
  readonly renderLinkPrompt?: (ctx: DimeLinkPromptContext) => ReactNode;
}

/** What `renderLinkPrompt` receives. */
export interface DimeLinkPromptContext {
  /** Continue to the terminal (with or without having linked). */
  readonly dismiss: () => void;
  /**
   * Link a platform identity to the signed-in account: the host supplies the
   * platform token it alone can mint (Paradex terminal token / Paradigm
   * embed token — mint it fresh, 5-minute TTL); the package supplies the
   * Clerk-derived DIME JWT and performs POST /v1/identity/links. Rejects
   * with a coded `DimeApiError` (`IDENTITY_ALREADY_LINKED`,
   * `ACCOUNT_NOT_ELIGIBLE`, `LINK_LIMIT_REACHED`, 401 for a bad/expired
   * platform token).
   */
  readonly linkIdentity: (platformToken: string) => Promise<IdentityLink>;
}

type SilentPhase = 'trying' | 'ok' | 'failed';

export function DimeCloudApp({
  clerk,
  config,
  renderPhase,
  agentEffects,
  compact,
  hideAuthShell,
  silentExchange,
  renderLinkPrompt,
}: DimeCloudAppProps): JSX.Element {
  const { environment, apiUrl, isSatellite, sessionCache } =
    useDimeTerminalContext();

  // One silent attempt per (apiUrl, accountId); 'failed' when no silent
  // exchange is given so the Clerk path renders immediately with no
  // intermediate state.
  const [silent, setSilent] = useState<SilentPhase>(
    silentExchange != null ? 'trying' : 'failed',
  );
  // The attempt PROMISE is memoized (keyed on meaningful inputs, not the
  // silentExchange object identity — hosts re-memoize it without semantic
  // change) so that:
  //  - StrictMode's mount → cleanup → mount re-subscribes to the same
  //    in-flight attempt. A run-once ref guard strands here: run 2 bails
  //    early while run 1's subscription was already cancelled, leaving
  //    'trying' forever with no path to the terminal OR the Clerk fallback.
  //  - the exchange still runs exactly once per attempt — each invocation is
  //    a real host-backend mint (for Paradex a paid, rate-limited kms:Sign),
  //    so simply dropping the guard (double-minting) is not acceptable.
  //  - a semantically new silentExchange (e.g. account switch) re-attempts.
  const attemptRef = useRef<Promise<unknown> | null>(null);
  const attemptKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (silentExchange == null) return;
    const url = resolveDimeApiUrl({ environment, apiUrl });
    const key = sessionCacheKey(url, silentExchange.accountId);
    if (attemptRef.current == null || attemptKeyRef.current !== key) {
      attemptKeyRef.current = key;
      // Through the provider session cache: the SAME key the terminal client
      // uses below, so this one exchange (or a still-fresh identity from a
      // previous mount - remounts cost zero) also covers the connection
      // resolve, sidecar auth and gateway handshake.
      attemptRef.current = sessionCache.getIdentity(key, () =>
        silentExchange.exchange(url),
      );
      // Re-attempts (key change after a failure) show the pending state
      // again; on the first attempt this is a no-op ('trying' already).
      setSilent('trying');
    }
    let alive = true;
    attemptRef.current.then(
      () => {
        if (alive) setSilent('ok');
      },
      () => {
        if (alive) setSilent('failed');
      },
    );
    return () => {
      alive = false;
    };
    // sessionCache is a stable provider ref — listed for exhaustive-deps.
  }, [silentExchange, environment, apiUrl, sessionCache]);

  // Packaged "Unlink account" action for the silent path (enabled by
  // silentExchange.mintPlatformToken): sever the link server-side with a
  // fresh platform token — self-unlink needs no DIME JWT — then drop this
  // session to the sign-in page. Clearing the memoized attempt matters: a
  // later effect re-run must start a fresh (now-refused) attempt, not
  // re-subscribe to the pre-unlink success and flip the UI back to 'ok'.
  const mintPlatformToken = silentExchange?.mintPlatformToken;
  const hostOnLogout = silentExchange?.onLogout;
  // Unlink failures keep the session (falling to sign-in anyway would
  // silently sign back in on the next mount — "unlink didn't work") and
  // surface an error notice instead of failing silently.
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  useEffect(() => {
    if (unlinkError == null) return;
    const timer = setTimeout(() => setUnlinkError(null), 8000);
    return () => clearTimeout(timer);
  }, [unlinkError]);
  const unlinkAccount = useMemo(() => {
    if (mintPlatformToken == null) return undefined;
    return async (): Promise<void> => {
      setUnlinkError(null);
      try {
        const platformToken = await mintPlatformToken();
        if (platformToken == null || platformToken.length === 0) {
          throw new Error('no platform token');
        }
        await selfUnlinkPlatformIdentity(
          resolveDimeApiUrl({ environment, apiUrl }),
          platformToken,
        );
      } catch {
        setUnlinkError(
          'Could not unlink this account. Check your connection and try again.',
        );
        return;
      }
      attemptRef.current = null;
      attemptKeyRef.current = null;
      // The session is over server-side; cached identity/grant must not
      // outlive it (a remount would rehydrate a dead session's credentials).
      sessionCache.clear();
      hostOnLogout?.();
      setSilent('failed');
    };
  }, [mintPlatformToken, hostOnLogout, environment, apiUrl, sessionCache]);

  if (silent === 'trying') {
    return (
      <Centered>
        <Muted>Connecting…</Muted>
      </Centered>
    );
  }

  if (silent === 'ok' && silentExchange != null) {
    return (
      <SilentShell>
        {unlinkError != null && (
          <UnlinkErrorToast role="alert">
            {unlinkError}
            <ToastDismiss
              type="button"
              aria-label="Dismiss"
              onClick={() => setUnlinkError(null)}
            >
              ✕
            </ToastDismiss>
          </UnlinkErrorToast>
        )}
        <DimeCloudTerminal
          environment={environment}
          apiUrl={apiUrl}
          exchange={silentExchange.exchange}
          renderPhase={renderPhase}
          agentEffects={agentEffects}
          compact={compact}
          config={{
            ...config,
            accountId: silentExchange.accountId,
            user: silentExchange.user,
            // Unlink (when enabled) replaces Log Out: severing the link is
            // the only escape action with real semantics on a tethered
            // session.
            onLogout: unlinkAccount ?? silentExchange.onLogout,
            logoutLabel: unlinkAccount != null ? 'Unlink account' : undefined,
          }}
        />
      </SilentShell>
    );
  }

  // ClerkProvider is above us in <DimeTerminalProvider> — Show/SignIn/useAuth
  // all find it via context.
  return (
    <>
      <Show when="signed-out">
        {isSatellite ? (
          <SatelliteSignInPrompt />
        ) : (
          <Centered>
            {/* Clerk's prebuilt widget = email + enabled socials in one.
                `virtual` routing keeps the wizard in-place without touching
                the host URL — essential in embedded contexts (drawer/iframe)
                where hash or path routing would hijack the host's location.
                Cast: RoutingStrategy includes 'virtual' but RoutingOptions
                lags behind in @clerk/shared@4 — runtime support is present. */}
            <SignIn
              routing={'virtual' as 'hash'}
              oauthFlow="popup"
              appearance={clerk?.appearance ?? DEFAULT_CLERK_APPEARANCE}
            />
          </Centered>
        )}
      </Show>
      <Show when="signed-in">
        <ClerkSignedIn
          config={config}
          renderPhase={renderPhase}
          agentEffects={agentEffects}
          compact={compact}
          hideAuthShell={hideAuthShell}
          renderLinkPrompt={renderLinkPrompt}
        />
      </Show>
    </>
  );
}

/**
 * Satellite sign-in prompt: instead of rendering the `<SignIn>` widget (which
 * Clerk rejects on satellite domains), show a link that navigates to the
 * primary domain for sign-in. `buildSignInUrl()` appends the current page URL
 * as the redirect target, so Clerk sends the user back after sign-in.
 */
function SatelliteSignInPrompt(): JSX.Element {
  const { buildSignInUrl } = useClerk();
  return (
    <Centered>
      <SignInLink href={buildSignInUrl()}>Sign in to Dime Terminal</SignInLink>
    </Centered>
  );
}

/**
 * Signed-in Clerk path: frame the flow with the auth shell (email + Log Out;
 * suppressed via `hideAuthShell` for embedded hosts with their own chrome)
 * and run the cloud flow on the Clerk exchange. The user never types a
 * gateway URL or token — the grant is resolved server-side and held in memory
 * only, so signing out simply unmounts this subtree and everything is gone.
 */
function ClerkSignedIn({
  config,
  renderPhase,
  agentEffects,
  compact,
  hideAuthShell,
  renderLinkPrompt,
}: Pick<
  DimeCloudAppProps,
  | 'config'
  | 'renderPhase'
  | 'agentEffects'
  | 'compact'
  | 'hideAuthShell'
  | 'renderLinkPrompt'
>): JSX.Element {
  const { environment, apiUrl, sessionCache } = useDimeTerminalContext();
  const { user } = useUser();
  const { signOut, getToken } = useAuth();
  // Pass a no-op callback so Clerk clears the session without navigating.
  // Without the callback, signOut() redirects to afterSignOutUrl (or "/"),
  // which causes a full page reload — fatal in embedded contexts.
  // The session cache is dropped first: the provider outlives the signed-in
  // subtree, and a signed-out user's still-valid identity/grant must not
  // wait out their TTLs in a live cache.
  const handleSignOut = useCallback(() => {
    sessionCache.clear();
    void signOut(() => undefined);
  }, [signOut, sessionCache]);
  const exchange = useMemo(
    () => createClerkTokenExchange(getToken),
    [getToken],
  );
  // Clerk profile → sidebar account button + menu header + greeting. Without
  // it the terminal derives a name from the gateway URL, which behind the
  // shared proxy is "gateway." for everyone. Memoized on the scalar fields so
  // the terminal config isn't rebuilt every render.
  const displayName = user?.firstName ?? user?.fullName ?? undefined;
  const email = user?.primaryEmailAddress?.emailAddress;
  const imageUrl = user?.imageUrl;
  const userProfile = useMemo<TerminalConfig['user']>(
    () => ({
      displayName,
      email,
      avatarUrl: imageUrl != null && imageUrl.length > 0 ? imageUrl : undefined,
    }),
    [displayName, email, imageUrl],
  );
  // The write half of the profile: Clerk owns the identity, so the terminal
  // renders the field and this performs the update. `firstName` because that is
  // the field the read above prefers, so a save and the next read agree.
  //
  // Clerk's `update` refreshes its own user object, which flows back through
  // `useUser()` → `userProfile` → the terminal, so the new name appears without
  // any local copy of it.
  const updateProfile = useCallback(
    async (patch: { readonly displayName: string }): Promise<void> => {
      if (user == null) throw new Error('Not signed in.');
      await user.update({ firstName: patch.displayName });
    },
    [user],
  );

  // Link-prompt gate: shown once per signed-in mount, before the terminal.
  const [linkPromptDismissed, setLinkPromptDismissed] = useState(false);
  const dismissLinkPrompt = useCallback(() => setLinkPromptDismissed(true), []);
  // The package half of linking: mint a fresh DIME JWT via the Clerk
  // exchange and POST the host-supplied platform token to
  // /v1/identity/links. The host half (minting the platform token) lives in
  // the host's prompt component.
  const linkIdentity = useCallback(
    (platformToken: string) =>
      linkPlatformIdentity(
        resolveDimeApiUrl({ environment, apiUrl }),
        exchange,
        platformToken,
      ),
    [environment, apiUrl, exchange],
  );

  const body =
    user == null ? (
      <Centered>
        <Muted>Loading…</Muted>
      </Centered>
    ) : renderLinkPrompt != null && !linkPromptDismissed ? (
      <>{renderLinkPrompt({ dismiss: dismissLinkPrompt, linkIdentity })}</>
    ) : (
      <DimeCloudTerminal
        environment={environment}
        apiUrl={apiUrl}
        exchange={exchange}
        renderPhase={renderPhase}
        agentEffects={agentEffects}
        compact={compact}
        config={{
          ...config,
          accountId: user.id,
          user: userProfile,
          onUpdateProfile: updateProfile,
          onLogout: handleSignOut,
        }}
      />
    );

  if (hideAuthShell === true) return body;
  return (
    <AuthShell email={email} onLogout={handleSignOut}>
      {body}
    </AuthShell>
  );
}

// Positioning context for the silent path so the unlink-error toast can
// overlay the terminal without a global notification system.
const SilentShell = styled.div`
  position: relative;
  height: 100%;
`;

const UnlinkErrorToast = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(420px, calc(100% - 24px));
  padding: 10px 14px;
  border: 1px solid var(--border-color, #d7dade);
  border-left: 3px solid #ef4444;
  border-radius: 8px;
  background: var(--bg-color-container, #f4f5f7);
  color: var(--text-primary, #16191c);
  font-size: 0.85rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
`;

const ToastDismiss = styled.button`
  flex: none;
  border: none;
  background: none;
  color: var(--text-secondary, #5f666d);
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  padding: 2px;
  &:hover {
    color: var(--text-primary, #16191c);
  }
`;

const Centered = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

const Muted = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const SignInLink = styled.a`
  font-size: 0.9rem;
  color: inherit;
  text-decoration: none;
  padding: 8px 16px;
  border: 1px solid currentColor;
  border-radius: 6px;
  opacity: 0.7;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;
